const MENU_ICON_SELECTOR = '.fj-civic-menu-icon';
const MENU_COMPOSITION_SELECTOR = [
    '.fj-civic-header .fj-civic-nav',
    '.fj-civic-navigation .fj-civic-nav',
].join(', ');
const MENU_ENHANCED_ATTRIBUTE = 'data-fj-header-menu-enhanced';
const MENU_VIEWPORT_QUERY = '(max-width: 900px)';

let nextMenuId = 0;

function directMenuLinks(menu: HTMLElement): HTMLAnchorElement[] {
    return Array.from(menu.children).filter((child): child is HTMLAnchorElement => (
        child.tagName === 'A'
    ));
}

function appendAudienceLoginLink(
    ownerDocument: Document,
    panel: HTMLElement,
    href: string | undefined,
    label: string
): void {
    if (!href || href === '#') return;
    const link = ownerDocument.createElement('a');
    link.href = href;
    link.className = 'fj-header-menu-panel__audience-link';
    link.textContent = label;
    panel.append(link);
}

function closeHeaderMenu(trigger: HTMLButtonElement, panel: HTMLElement, restoreFocus = false): void {
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', 'Open site navigation');
    trigger.classList.remove('is-open');
    panel.hidden = true;
    if (restoreFocus) trigger.focus({ preventScroll: true });
}

export function resolveGovernedMenuSource(icon: HTMLElement): HTMLElement | null {
    if (!icon.closest('.fj-civic-header')) return null;
    const experienceRoot = icon.closest<HTMLElement>('.fj-experience-public');
    if (!experienceRoot) return null;

    const candidates = Array.from(
        experienceRoot.querySelectorAll<HTMLElement>(MENU_COMPOSITION_SELECTOR)
    );
    return candidates.length === 1 ? candidates[0] : null;
}

export function reconcileHeaderMenuViewport(
    trigger: HTMLButtonElement,
    panel: HTMLElement,
    sourceLinks: HTMLAnchorElement[],
    isMobile: boolean
): void {
    const activeElement = trigger.ownerDocument.activeElement;
    const panelLinks = directMenuLinks(panel);
    const panelLinkIndex = panelLinks.indexOf(activeElement as HTMLAnchorElement);
    const sourceLinkIndex = sourceLinks.indexOf(activeElement as HTMLAnchorElement);

    closeHeaderMenu(trigger, panel);

    if (isMobile) {
        if (sourceLinkIndex >= 0) {
            trigger.focus({ preventScroll: true });
        }
        return;
    }

    if (activeElement === trigger || panelLinkIndex >= 0) {
        (sourceLinks[panelLinkIndex] ?? sourceLinks[0])?.focus({ preventScroll: true });
    }
}

function enhanceHeaderMenuIcon(icon: HTMLElement): void {
    if (icon.hasAttribute(MENU_ENHANCED_ATTRIBUTE)) return;

    const header = icon.closest<HTMLElement>('.fj-civic-header');
    const menu = resolveGovernedMenuSource(icon);
    const links = menu ? directMenuLinks(menu) : [];
    if (!header || !menu || links.length === 0) return;

    const ownerDocument = icon.ownerDocument;
    const trigger = ownerDocument.createElement('button');
    Array.from(icon.attributes).forEach(({ name, value }) => {
        if (name !== 'aria-hidden') {
            trigger.setAttribute(name, value);
        }
    });
    trigger.type = 'button';
    trigger.classList.remove('fj-builder-control-node');
    trigger.setAttribute(MENU_ENHANCED_ATTRIBUTE, 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', 'Open site navigation');

    const panel = ownerDocument.createElement('div');
    panel.id = `fj-header-menu-${++nextMenuId}`;
    panel.className = 'fj-header-menu-panel';
    panel.hidden = true;
    panel.setAttribute('data-fj-header-menu-panel', '');
    links.forEach((link) => panel.append(link.cloneNode(true)));
    const candidateLoginUrl = icon.dataset.candidateLoginUrl;
    const employerLoginUrl = icon.dataset.employerLoginUrl;
    if (candidateLoginUrl || employerLoginUrl) {
        const audienceLabel = ownerDocument.createElement('strong');
        audienceLabel.className = 'fj-header-menu-panel__audience-label';
        audienceLabel.textContent = 'Sign in';
        panel.append(audienceLabel);
        appendAudienceLoginLink(ownerDocument, panel, candidateLoginUrl, 'Candidate sign in');
        appendAudienceLoginLink(ownerDocument, panel, employerLoginUrl, 'Employer sign in');
    }

    trigger.setAttribute('aria-controls', panel.id);
    menu.classList.add('fj-header-menu-source');
    menu.closest<HTMLElement>('.fj-experience-div, .fj-builder-include-div')
        ?.classList.add('fj-header-menu-source-slot');
    header.querySelector<HTMLElement>('.fj-civic-button')
        ?.closest<HTMLElement>('.fj-experience-div, .fj-builder-include-div')
        ?.classList.add('fj-header-cta-slot');
    icon.closest<HTMLElement>('.fj-experience-div, .fj-builder-include-div')
        ?.classList.add('fj-header-menu-trigger-slot');
    menu.append(panel);
    icon.replaceWith(trigger);

    const closeOtherMenus = (): void => {
        ownerDocument.querySelectorAll<HTMLButtonElement>(`${MENU_ICON_SELECTOR}[aria-expanded="true"]`).forEach((candidate) => {
            if (candidate === trigger) return;
            const candidatePanelId = candidate.getAttribute('aria-controls');
            const candidatePanel = candidatePanelId ? ownerDocument.getElementById(candidatePanelId) : null;
            if (candidatePanel) closeHeaderMenu(candidate, candidatePanel);
        });
    };

    trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const opening = trigger.getAttribute('aria-expanded') !== 'true';
        closeOtherMenus();
        if (!opening) {
            closeHeaderMenu(trigger, panel);
            return;
        }
        trigger.setAttribute('aria-expanded', 'true');
        trigger.setAttribute('aria-label', 'Close site navigation');
        trigger.classList.add('is-open');
        panel.hidden = false;
        panel.querySelector<HTMLAnchorElement>('a[href]')?.focus({ preventScroll: true });
    });

    ownerDocument.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || panel.hidden) return;
        event.preventDefault();
        closeHeaderMenu(trigger, panel, true);
    });

    ownerDocument.addEventListener('click', (event) => {
        const target = event.target as Node;
        if (panel.hidden || trigger.contains(target) || panel.contains(target)) return;
        closeHeaderMenu(trigger, panel);
    });

    ownerDocument.defaultView
        ?.matchMedia?.(MENU_VIEWPORT_QUERY)
        .addEventListener?.('change', (event) => {
            reconcileHeaderMenuViewport(trigger, panel, links, event.matches);
        });
}

export function enhanceGovernedHeaderMenus(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(MENU_ICON_SELECTOR).forEach(enhanceHeaderMenuIcon);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceGovernedHeaderMenus(), { once: true });
} else {
    enhanceGovernedHeaderMenus();
}
